import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Right-to-left product ticker for the public landing page.
 * Data source: GET /api/landing/products  (public, in-stock, published only)
 * Tiles are intentionally non-interactive — visual trust signal only.
 * The header "Browse catalog & pricing" pill remains the sole conversion path.
 */
export default function LandingTicker() {
  const [names, setNames] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/landing/products`)
      .then((r) => { if (!cancelled) setNames(r.data?.names || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Hide ticker completely if nothing to show — never render an empty rail.
  if (names.length === 0) return null;

  // iter_138 — de-duplicate names. Backend returns every published product
  // row, but a catalog often has "SS-31" and "SS-31 10mg" as separate rows
  // (or literal "TZ-2A" duplicates from data cleanup). For the marketing
  // ticker we only want ONE entry per product family, so we normalize each
  // name (strip parens, strip mg/ug suffix, drop punctuation) and keep the
  // FIRST occurrence — the backend already sorts by `featured DESC, name ASC`
  // so featured items win.
  const seen = new Set();
  const unique = [];
  for (const raw of names) {
    const key = String(raw)
      .toLowerCase()
      .replace(/\([^)]*\)/g, "")     // strip everything inside parens
      .replace(/\d+\s*m?g\b/gi, "")  // strip mg / ug / g dose suffixes
      .replace(/[^a-z0-9]/g, "")     // drop remaining punctuation & spaces
      .trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(raw);
  }

  // Duplicate list so the marquee loops seamlessly (bbk-marquee animates 0 → -50%).
  const loop = [...unique, ...unique];

  return (
    <section
      className="relative bg-charcoal text-bone border-y border-charcoal overflow-hidden"
      aria-label="Featured catalog products"
      data-testid="landing-ticker"
    >
      {/* Left / right fade masks so the tape edges dissolve into the bg */}
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-24 z-10"
        style={{ background: "linear-gradient(to right, #1A1A1A, transparent)" }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10"
        style={{ background: "linear-gradient(to left, #1A1A1A, transparent)" }}
        aria-hidden
      />

      {/* Small label pinned above the tape */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-6 pb-3 flex items-center gap-4">
        <span className="text-[12px] uppercase tracking-[0.32em] text-bone/85 font-mono">
          In stock now
        </span>
        <span className="h-px flex-1 bg-bone/15" aria-hidden />
        <span className="text-[12px] uppercase tracking-[0.32em] text-bone/85 font-mono">
          {unique.length} kits
        </span>
      </div>

      {/* The tape */}
      <div
        className="group py-5"
        onMouseEnter={(e) => (e.currentTarget.dataset.paused = "1")}
      >
        <div className="flex w-max animate-bbk-marquee-slow group-hover:[animation-play-state:paused]">
          {loop.map((n, i) => (
            <TickerTile key={`${n}-${i}`} name={n} />
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-6 pt-3">
        <span className="text-[12px] uppercase tracking-[0.32em] text-bone/85 font-mono">
          Sign in to view pricing · Members only
        </span>
      </div>
    </section>
  );
}

function TickerTile({ name }) {
  return (
    <div
      className="flex items-center gap-6 pr-8"
      data-testid="landing-ticker-tile"
      aria-hidden
    >
      <span className="font-serif text-2xl sm:text-3xl tracking-tight text-bone whitespace-nowrap">
        {name}
      </span>
      <Diamond />
    </div>
  );
}

function Diamond() {
  return (
    <span className="inline-block" aria-hidden>
      <svg width="10" height="10" viewBox="0 0 10 10">
        <rect
          x="5"
          y="0.5"
          width="6"
          height="6"
          transform="rotate(45 5 5)"
          fill="none"
          stroke="#4A0E17"
          strokeWidth="1.25"
        />
      </svg>
    </span>
  );
}
